import { AR_CANVAS_HEIGHT, AR_CANVAS_WIDTH, type ARConfig, type ARModule } from './types';
import { captureHealth, type RemoteVideoStats } from '../lib/captureHealth';
import { devError, devLog, devWarn } from '../lib/devLog';
import { noteEvent } from '../lib/diagnostics';

const TAG = '[ar:remote]';

/**
 * The AR rendered on another machine, streamed in over WebRTC.
 *
 * ## Why this exists
 *
 * Camera Kit cannot render a lens on this panel at a usable rate. Measured on
 * the unit: 2-7fps for every lens tried, including Snap's own stock demos, with
 * the GPU at 33-61% and idle CPU cores — it blocks inside LensCore rather than
 * running out of capacity. Our own code was cleared first: the recorder, the
 * compositor and the detector were each disabled independently with no effect.
 * See docs/KIOSK_PERFORMANCE.md.
 *
 * The same lens runs at a flat 60fps on a laptop. So the laptop renders it and
 * this panel displays the result: 23-30fps, ~33ms of delay, decoded in hardware
 * (MediaCodecVideoDecoder). One direction only — the camera is on the laptop
 * too, which deletes the return leg that never worked.
 *
 * ## Why it is an ARModule
 *
 * Because then nothing else changes. `CaptureScreen` asks for a canvas and a
 * trigger callback; where the pixels came from is not its concern. The logo
 * compositor, the pre-roll, the recorder, the upload queue and the whole flow
 * carry on untouched — and with the lens gone from this device there is ample
 * headroom for them (a canvas, captureStream, a VP8 recorder and a live mic
 * together sustained 52-60fps here in isolation).
 *
 * ## Shape
 *
 *   panel (this)                        laptop (`prototype/mirror/stage.html`)
 *   ─────────────                       ──────────────────────────────────────
 *   offer, recvonly video ─────────────► camera + Camera Kit + lens
 *   data channel "control" ◄───────────► trigger, costume visibility
 *   <video> → canvas → the rest of the app
 *
 * Signalling goes through the native relay in `KioskAssetServer` because this
 * page is on a secure origin and the laptop is plain HTTP on the LAN — a direct
 * fetch would be blocked as mixed content. See that file.
 */

/** Where the laptop's signalling lives, proxied by the shell. */
const RELAY = '/relay/signal';

export function createRemoteModule(): ARModule {
  const canvas = document.createElement('canvas');
  canvas.width = AR_CANVAS_WIDTH;
  canvas.height = AR_CANVAS_HEIGHT;
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.objectFit = 'cover';
  canvas.style.display = 'block';
  const ctx = canvas.getContext('2d', { alpha: false });

  const video = document.createElement('video');
  video.autoplay = true;
  video.playsInline = true;
  video.muted = true;

  let pc: RTCPeerConnection | null = null;
  let control: RTCDataChannel | null = null;
  let cfg: ARConfig | null = null;
  let rafId: number | null = null;
  let destroyed = false;
  /** A frame has been painted. */
  let ready = false;
  /** The render machine has reset the lens for this guest (or we stopped waiting). */
  let lensFresh = false;
  /** onReady has been delivered for the current init(). */
  let reported = false;
  /** `begin` could not be sent yet because the control channel was not open. */
  let beginPending = false;
  let readyFallback: number | null = null;
  /** When the offer went out; an answer that never comes leaves the link 'new' forever. */
  let offerAt = 0;
  let polling = false;
  /** Paints of the <video> into the canvas, for the capture-health sampler. */
  let paints = 0;
  /** ICE candidates that arrived before the answer did. */
  const pendingCandidates: RTCIceCandidateInit[] = [];

  const post = (obj: unknown): Promise<unknown> =>
    fetch(`${RELAY}?to=host&msg=${encodeURIComponent(JSON.stringify(obj))}`).catch(() => undefined);

  function reportError(err: Error): void {
    if (destroyed) return;
    devError(`${TAG} ${err.message}`);
    cfg?.onError(err);
  }

  /**
   * Paint the incoming video into our canvas, cover-fit.
   *
   * The extra copy is deliberate: the contract hands `CaptureScreen` a canvas,
   * and the compositor and the recorder both read from it. A <video> cannot
   * stand in for that, and the copy is a hardware-decoded frame blitted once per
   * paint — cheap next to what this panel was doing before.
   */
  function paint(): void {
    if (destroyed) return;
    if (ctx && video.videoWidth > 0) {
      const vw = video.videoWidth;
      const vh = video.videoHeight;
      const scale = Math.max(canvas.width / vw, canvas.height / vh);
      const dw = vw * scale;
      const dh = vh * scale;
      ctx.drawImage(video, (canvas.width - dw) / 2, (canvas.height - dh) / 2, dw, dh);
      paints += 1;

      if (!ready) {
        ready = true;
        noteEvent('remote AR: first frame');
        maybeReport();
      }
    }
    rafId = requestAnimationFrame(paint);
  }

  /**
   * Ready means two things at once: a frame is on the canvas, and the render
   * machine has reset the lens for THIS guest. The second matters because the
   * lens transforms on its own whenever it sees an open mouth — a guest
   * talking through the instruction clip met their own mask before they had
   * seen their face (2026-09-19). The reset is asked for at init() and takes
   * ~0.3-0.4s; the screen waits for it rather than showing a stale lens.
   */
  function maybeReport(): void {
    if (!cfg || reported || destroyed) return;
    if (!ready || !lensFresh) return;
    reported = true;
    if (readyFallback !== null) clearTimeout(readyFallback);
    readyFallback = null;
    noteEvent('remote AR: lens fresh, picture live');
    cfg.onReady();
  }

  /**
   * Is the link something a guest can be put in front of? A closed peer
   * connection is obvious; a closed control channel on a still-"connected"
   * peer is the sneaky case — the render machine's page reloaded, its side
   * closed, and ICE has not noticed yet. Sending `begin` into that channel
   * gets no answer, ever (2026-09-20, run 14: a dark screen until the idle
   * timeout).
   */
  function linkDead(): boolean {
    if (!pc) return false;
    const s = pc.connectionState;
    if (s === 'failed' || s === 'closed' || s === 'disconnected') return true;
    if (s === 'connected' && control && control.readyState !== 'open' && control.readyState !== 'connecting') return true;
    // An offer posted while the render machine's server was down is never
    // answered: the connection sits in 'new' with nothing coming (run 14).
    if ((s === 'new' || s === 'connecting') && offerAt && Date.now() - offerAt > 6000) return true;
    return false;
  }

  /** Start over on a fresh connection; the caller decides what to send after. */
  async function reconnect(why: string): Promise<void> {
    devWarn(`${TAG} ${why} — reconnecting`);
    noteEvent(`remote AR: ${why} — reconnecting`);
    dropConnection();
    await connect();
  }

  /**
   * Ask the render machine to reset the lens for this guest, then tell us.
   *
   * The watchdog behind it covers every way the answer can fail to come: a
   * render machine on older code (no `ready` message) goes live anyway as
   * long as frames are moving; a link that shows no new frames is torn down
   * and rebuilt, once, and `begin` is sent again on the new one.
   */
  function sendBegin(): void {
    if (control?.readyState === 'open') {
      beginPending = false;
      control.send(JSON.stringify({ type: 'begin' }));
    } else {
      beginPending = true; // sent from control.onopen
    }
    if (readyFallback !== null) clearTimeout(readyFallback);
    const paintsAtBegin = paints;
    readyFallback = window.setTimeout(() => {
      readyFallback = null;
      if (lensFresh || destroyed || reported) return;
      const moving = paints > paintsAtBegin + 5;
      if (moving && !linkDead()) {
        devWarn(`${TAG} no 'ready' from the render machine within 2.5s — going live without the lens reset`);
        lensFresh = true;
        maybeReport();
        return;
      }
      if (reconnected) {
        reportError(new Error('no picture from the render machine'));
        return;
      }
      reconnected = true;
      void reconnect(`no frames from the render machine (link ${pc?.connectionState ?? 'none'}, control ${control?.readyState ?? 'none'})`)
        .then(() => { if (!destroyed) sendBegin(); })
        .catch((err: unknown) => reportError(err instanceof Error ? err : new Error(String(err))));
    }, 2500);
  }
  /** One reconnect per init(); a second failure is an error the flow should show. */
  let reconnected = false;

  /**
   * Count frames the <video> actually presents, for the capture-health
   * sampler. This is the "is the stream itself still moving" signal: if it
   * keeps rising while the recorded clip freezes, the stall is on this panel
   * and not on the render machine.
   */
  function countPresentedFrames(): void {
    const v = video as HTMLVideoElement & {
      requestVideoFrameCallback?: (cb: () => void) => number;
    };
    if (typeof v.requestVideoFrameCallback !== 'function') return;
    const onFrame = (): void => {
      if (destroyed) return;
      captureHealth.remoteFrame();
      v.requestVideoFrameCallback?.(onFrame);
    };
    v.requestVideoFrameCallback(onFrame);
  }

  async function addCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!pc) return;
    // addIceCandidate throws without a remote description, and swallowing that
    // loses the candidate — on a point-to-point link with few candidates,
    // losing one loses all of them and the connection sits at `new` forever.
    if (!pc.remoteDescription) {
      pendingCandidates.push(candidate);
      return;
    }
    await pc.addIceCandidate(candidate).catch((e: unknown) => devWarn(`${TAG} ice`, e));
  }

  async function flushCandidates(): Promise<void> {
    if (!pc) return;
    while (pendingCandidates.length) {
      const c = pendingCandidates.shift();
      if (c) await pc.addIceCandidate(c).catch((e: unknown) => devWarn(`${TAG} ice`, e));
    }
  }

  async function poll(): Promise<void> {
    if (polling) return;
    polling = true;
    while (!destroyed) {
      try {
        const res = await fetch(`${RELAY}?as=kiosk`);
        const msgs: string[] = await res.json();
        for (const raw of msgs) {
          const m = JSON.parse(raw) as { sdp?: RTCSessionDescriptionInit; candidate?: RTCIceCandidateInit };
          if (!pc) continue;
          if (m.sdp) {
            await pc.setRemoteDescription(m.sdp);
            await flushCandidates();
          } else if (m.candidate) {
            await addCandidate(m.candidate);
          }
        }
      } catch {
        // The laptop may not be up yet; keep asking.
      }
      await new Promise((r) => setTimeout(r, 250));
    }
    polling = false;
  }

  /** Close the connection but keep the module usable; `connect()` starts over. */
  function dropConnection(): void {
    control?.close();
    control = null;
    pc?.close();
    pc = null;
    video.srcObject = null;
    ready = false;
    offerAt = 0;
    pendingCandidates.length = 0;
  }

  /**
   * Negotiate and start receiving. Idempotent: a second call while a
   * connection exists does nothing, so `preconnect()` followed by `init()`
   * negotiates once. Needs no container and no callbacks — the picture lands
   * in the module's own canvas either way, and `init()` reports readiness when
   * it arrives, or at once if it already has.
   */
  async function connect(): Promise<void> {
    if (pc) return;
    destroyed = false;

    await fetch('/relay/reset').catch(() => undefined);
    if (destroyed) return;

    pc = new RTCPeerConnection({ iceServers: [] });

      pc.ontrack = (e) => {
        const stream = e.streams[0];
        if (!stream || video.srcObject === stream) return;
        video.srcObject = stream;
        void video.play().catch((err: unknown) => devWarn(`${TAG} play`, err));
        countPresentedFrames();
      };
      pc.onicecandidate = (e) => {
        if (e.candidate) void post({ candidate: e.candidate });
      };
      pc.onconnectionstatechange = () => {
        devLog(`${TAG} connection ${pc?.connectionState}`);
        if (pc?.connectionState === 'failed') {
          reportError(new Error('lost the connection to the render machine'));
        }
      };

      // Receive video; talk back over a data channel. The receiver is asked to
      // play out with no added buffering: this is a mirror, and every frame
      // held back is felt as lag. Chromium honours the hint on a LAN this
      // clean (0 loss, ~2ms), where the jitter buffer buys nothing.
      const videoTransceiver = pc.addTransceiver('video', { direction: 'recvonly' });
      try {
        (videoTransceiver.receiver as RTCRtpReceiver & { playoutDelayHint?: number }).playoutDelayHint = 0;
      } catch {
        /* older WebView — default buffering */
      }
      control = pc.createDataChannel('control');
      control.onopen = () => {
        devLog(`${TAG} control channel open`);
        if (beginPending) sendBegin();
      };
      control.onmessage = (e) => {
        try {
          const m = JSON.parse(String(e.data)) as { type?: string };
          if (m.type === 'trigger') {
            noteEvent('remote AR: trigger from the render machine');
            cfg?.onTrigger();
          } else if (m.type === 'ready') {
            lensFresh = true;
            maybeReport();
          }
        } catch {
          devWarn(`${TAG} unparseable control message`, e.data);
        }
      };

      // H.264 is the only codec this panel decodes in hardware. Left to itself
      // WebRTC picks VP8, which decodes in software here and built a 15s backlog.
      const caps = RTCRtpReceiver.getCapabilities('video');
      if (caps) {
        const isH264 = (c: { mimeType: string }): boolean => /h264/i.test(c.mimeType);
        const ordered = [...caps.codecs.filter(isH264), ...caps.codecs.filter((c) => !isH264(c))];
        for (const t of pc.getTransceivers()) {
          try {
            t.setCodecPreferences(ordered);
          } catch {
            // Older WebView — the default order still works, just slower.
          }
        }
      }

      await pc.setLocalDescription(await pc.createOffer());
      await post({ sdp: pc.localDescription });
      offerAt = Date.now();
      void poll();

      if (rafId === null) paint();
    }

  return {
    preconnect(): void {
      // The attract tap, or the result screen (a retake may follow). The
      // instruction clip runs 3.1s; the connect and the render machine's lens
      // reset take 1.2-2s. Started here, the capture screen mounts onto a
      // picture that is already moving, instead of onto "Opening the camera…"
      // — measured 2026-09-18.
      const state = pc?.connectionState;
      devLog(`${TAG} preconnect (existing link: ${state ?? 'none'}, control ${control?.readyState ?? 'none'})`);
      if (pc && !linkDead()) {
        // Still connected from an earlier screen. Have the lens reset now,
        // out of sight, so `begin` at the capture screen has nothing to wait for.
        if (control?.readyState === 'open') control.send(JSON.stringify({ type: 'prepare' }));
        return;
      }
      if (pc) dropConnection();
      noteEvent('remote AR: connecting ahead of the capture screen');
      void connect().catch((err: unknown) => devWarn(`${TAG} preconnect`, err));
    },

    async init(container: HTMLElement, config: ARConfig): Promise<void> {
      cfg = config;
      devLog(`${TAG} init (connected: ${pc?.connectionState ?? 'no'}, picture: ${ready})`);
      container.appendChild(canvas);

      // A pre-connect that died in the meantime — the render machine's page
      // reloaded, the cable was knocked — starts over rather than being
      // reported as ready. See linkDead for the case ICE has not noticed yet.
      if (linkDead()) {
        devWarn(`${TAG} pre-connected link unusable (${pc?.connectionState}, control ${control?.readyState}) — reconnecting`);
        dropConnection();
      }

      await connect();
      if (destroyed) return;

      // Ready is reported by maybeReport() once a frame is painted AND the
      // render machine has reset the lens for this guest — see there. With a
      // pre-connect the frame is usually already here and only the reset is
      // outstanding, ~0.3-0.4s. The watchdog in sendBegin covers the rest.
      lensFresh = false;
      reported = false;
      reconnected = false;
      sendBegin();
    },

    start(): void {
      devLog(`${TAG} start`);
    },

    stop(): void {
      devLog(`${TAG} stop`);
    },

    setCostumeVisible(visible: boolean): void {
      // Forwarded so the render machine can hold the costume until the fog is at
      // its heaviest, exactly as the local module does.
      if (control?.readyState === 'open') {
        control.send(JSON.stringify({ type: 'costume', visible }));
      }
    },

    arm(): void {
      // The grace period is over: from here a trigger counts. The render
      // machine resets the lens now if the guest's arrival set it off, so the
      // gesture that counts starts from a clean face.
      if (control?.readyState === 'open') control.send(JSON.stringify({ type: 'armed' }));
    },

    startTransition(): void {
      // The render machine plays the fog into the stream. This panel renders
      // none of it: the fog was the last heavy GPU job here and it starved the
      // recording's canvas capture at exactly this moment (docs/HANDOFF.md).
      // If the channel is not open the flow's timers still carry the guest
      // through; that take simply has no fog.
      if (control?.readyState === 'open') {
        control.send(JSON.stringify({ type: 'transition' }));
        noteEvent('remote AR: transition sent to the render machine');
      } else {
        devWarn(`${TAG} control channel not open — transition not sent`);
      }
    },

    async getDebugStats(): Promise<RemoteVideoStats | null> {
      if (!pc) return null;
      const quality = typeof video.getVideoPlaybackQuality === 'function' ? video.getVideoPlaybackQuality() : null;
      const local = { videoTotalFrames: quality?.totalVideoFrames ?? 0, paints };
      try {
        const report = await pc.getStats();
        for (const s of report.values()) {
          const r = s as RTCInboundRtpStreamStats & {
            kind?: string;
            framesReceived?: number;
            framesDropped?: number;
            freezeCount?: number;
            totalFreezesDuration?: number;
            decoderImplementation?: string;
          };
          if (r.type === 'inbound-rtp' && r.kind === 'video') {
            return {
              framesDecoded: r.framesDecoded ?? 0,
              framesReceived: r.framesReceived ?? 0,
              framesDropped: r.framesDropped ?? 0,
              freezeCount: r.freezeCount ?? 0,
              totalFreezesDuration: r.totalFreezesDuration ?? 0,
              decoderImplementation: r.decoderImplementation ?? '?',
              ...local,
            };
          }
        }
      } catch {
        /* a closing connection — the local counters still say something */
      }
      return {
        framesDecoded: 0,
        framesReceived: 0,
        framesDropped: 0,
        freezeCount: 0,
        totalFreezesDuration: 0,
        decoderImplementation: '?',
        ...local,
      };
    },

    getCanvas(): HTMLCanvasElement {
      return canvas;
    },

    destroy(): void {
      devLog(`${TAG} destroy`);
      destroyed = true;
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = null;
      if (readyFallback !== null) clearTimeout(readyFallback);
      readyFallback = null;
      dropConnection();
      canvas.remove();
      cfg = null;
    },
  };
}
